import { lazy, Suspense, useCallback, useEffect } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { audioManager } from '../game/audio/audioManager';
import { HomeScreen } from '../screens/HomeScreen';
import { LevelScreen } from '../screens/LevelScreen';
import { LevelSelectScreen } from '../screens/LevelSelectScreen';
import { SettingsScreen } from '../screens/SettingsScreen';
import { useAppStore } from '../state/store';
import { Spinner } from '../ui/components';
import { useDocumentVisible, useResolvedPreferences } from './preferences';
import { useRoute, type Route } from './router';
import { UpdatePrompt } from './UpdatePrompt';

/** Developer tooling is code-split so it never reaches a production bundle's critical path. */
const DeveloperLabScreen = lazy(() =>
  import('../screens/DeveloperLabScreen').then((module) => ({
    default: module.DeveloperLabScreen,
  })),
);

export function App() {
  const { phase, settings, boot, setPaused, persistInterrupted, levelPhase } = useAppStore(
    useShallow((state) => ({
      phase: state.phase,
      settings: state.settings,
      boot: state.boot,
      setPaused: state.setPaused,
      persistInterrupted: state.persistInterrupted,
      levelPhase: state.levelPhase,
    })),
  );

  const [route, navigate] = useRoute();
  const preferences = useResolvedPreferences(settings);
  const visible = useDocumentVisible();

  useEffect(() => {
    void boot();
  }, [boot]);

  // Suspending the tab pauses play, saves the resume point and quietens audio; returning
  // leaves the game paused so the player is never dropped straight back into a live board.
  useEffect(() => {
    audioManager.setActive(visible);
    if (visible) return;
    if (levelPhase === 'ready') {
      setPaused(true);
      persistInterrupted();
    }
  }, [visible, levelPhase, setPaused, persistInterrupted]);

  useEffect(() => {
    const onPageHide = (): void => persistInterrupted();
    globalThis.addEventListener('pagehide', onPageHide);
    return () => globalThis.removeEventListener('pagehide', onPageHide);
  }, [persistInterrupted]);

  const go = useCallback((next: Route) => navigate(next), [navigate]);

  if (phase === 'booting') {
    return (
      <div className="ar-app">
        <div className="ar-screen" data-variant="centred">
          <Spinner label="Loading Arrows…" />
        </div>
      </div>
    );
  }

  return (
    <div className="ar-app">
      <UpdatePrompt />
      {renderRoute(route, go, preferences)}
    </div>
  );
}

function renderRoute(
  route: Route,
  go: (route: Route) => void,
  preferences: ReturnType<typeof useResolvedPreferences>,
) {
  switch (route.name) {
    case 'play':
      return (
        <LevelScreen
          levelNumber={route.level}
          theme={preferences.renderTheme}
          reducedMotion={preferences.reducedMotion}
          onExit={() => go({ name: 'home' })}
          onPlayLevel={(level) => go({ name: 'play', level })}
        />
      );
    case 'levels':
      return (
        <LevelSelectScreen
          onBack={() => go({ name: 'home' })}
          onPlay={(level) => go({ name: 'play', level })}
        />
      );
    case 'settings':
      return <SettingsScreen onBack={() => go({ name: 'home' })} />;
    case 'about':
      return <AboutScreen onBack={() => go({ name: 'home' })} />;
    case 'lab':
      return (
        <Suspense
          fallback={
            <div className="ar-screen" data-variant="centred">
              <Spinner label="Loading the generator lab…" />
            </div>
          }
        >
          <DeveloperLabScreen onBack={() => go({ name: 'home' })} />
        </Suspense>
      );
    case 'home':
      return (
        <HomeScreen
          onPlay={(level) => go({ name: 'play', level })}
          onLevels={() => go({ name: 'levels' })}
          onSettings={() => go({ name: 'settings' })}
          onLab={() => go({ name: 'lab' })}
        />
      );
  }
}

function AboutScreen({ onBack }: { onBack(): void }) {
  return (
    <div className="ar-screen" data-variant="centred">
      <div className="ar-stack">
        <h1 className="ar-subtitle">About Arrows</h1>
        <p className="ar-muted">
          Arrows is an offline puzzle game. Every level is produced by a deterministic generator, so
          the same level number always builds the same board on every device — no server and no
          downloads involved.
        </p>
        <p className="ar-muted">
          Nothing you do here leaves your device. There is no account, no analytics and no
          advertising.
        </p>
        <button type="button" className="ar-button" onClick={onBack}>
          Back
        </button>
      </div>
    </div>
  );
}
